import {
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  GKButton,
  GKCheckbox,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  RadioGroup,
  Show,
  SimpleLoader,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { AGREEMENT_SIGNATURE_SOURCE, KYC_OPTIONS } from "utils/constants";
import { PAYMENT_METHOD } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";
import { validateNotWhitespaceOnly } from "utils/validationSchema";

interface FormValues {
  kycType: string;
  agreement: string;
  codePrefix: string;
  invoiceSeries: string;
  paymentMethods: string;
  revenueWithGst: boolean;
  codeLength: number;
  agreementTemplate: string;
  customizeSetting: {
    kyc: boolean;
    agreement: boolean;
    riskprofiling: boolean;
  };
}

const WebsiteConfigurations = () => {
  useDocumentTitle("Investor Settings");

  const userData = useAppSelector((state) => state?.user?.userData);

  const [customAgreementTemplate, setCustomAgreementTemplate] = useState([]);

  const form = useForm<FormValues>({
    defaultValues: {
      kycType: "",
      agreement: "",
      codePrefix: "",
      invoiceSeries: "",
      paymentMethods: "",
      revenueWithGst: false,
      codeLength: 3,
      agreementTemplate: "",
      customizeSetting: {
        agreement: false,
        kyc: false,
        riskprofiling: false,
      },
    },
  });

  const { isFetching: customAgreementLoading } = useFetchQuery({
    key: ["ON_BOARDING_FLOW"],
    route: "/custom-agreement/",
    success: (response: any) => {
      setCustomAgreementTemplate(
        response?.data?.data.map((dataItem: { title: string; id: number }) => {
          return {
            name: dataItem.title,
            value: dataItem.id,
          };
        }),
      );
    },
  });

  const { refetch, isFetching } = useFetchQuery({
    key: ["GET_INVESTOR_PROFILE"],
    route: `/company/${userData?.companyId}/publish_settings/config/`,
    success: (data: any) => {
      form.reset({
        kycType: data?.data?.kycType || "",
        agreement: data?.data?.agreement || "",
        codePrefix: data?.data?.codePrefix || "",
        paymentMethods: data?.data?.paymentMethods || "",
        invoiceSeries: data?.data?.invoiceSeries || "",
        revenueWithGst: data?.data?.revenueWithGst || false,
        codeLength: data?.data?.codeLength || 3,
        agreementTemplate: data?.data?.agreementTemplate || "",
        customizeSetting: {
          kyc: data?.data?.customizeSetting?.kyc,
          agreement: data?.data?.customizeSetting?.agreement,
          riskprofiling: data?.data?.customizeSetting?.riskprofiling,
        },
      });
    },
  });

  const { mutate: updateInvestorProfile, isLoading: updateInvestorLoading } =
    useMutation({
      mutationKey: ["GET_INVESTOR_PROFILE_DOMAIN"],
      mutationFn: (data: any) =>
        axiosInstance.patch(
          `/company/${userData?.companyId}/publish_settings/config/`,
          data,
        ),
      onSuccess: () => refetch(),
    });

  const prefixWatch = form.watch(["codePrefix", "codeLength"]);

  if (isFetching) return <SimpleLoader />;

  return (
    <form
      onSubmit={form.handleSubmit((values: FormValues) =>
        updateInvestorProfile(values),
      )}
      id="investor-setting-form"
    >
      <Grid2 container spacing={2}>
        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Onboarding
          </Typography>
        </Grid2>

        <Grid2
          container
          size={9}
          spacing={2}
          display={"flex"}
          alignItems={"center"}
        >
          <Grid2 size={12}>
            <Controller
              name="customizeSetting.kyc"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <GKCheckbox
                      disableLeftPadding
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={"Include KYC in onboarding flow"}
                  labelPlacement="end"
                />
              )}
            />
          </Grid2>
          <Show.When isTrue={form.watch("customizeSetting.kyc")}>
            <Grid2 size={6}>
              <Controller
                name="kycType"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please select KYC type",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <GKSearchSelect
                    {...field}
                    disableClearable
                    requiredField
                    inputLabel="KYC Type"
                    options={KYC_OPTIONS}
                    onChange={(event, val) =>
                      field.onChange(val?.value || null)
                    }
                    value={
                      KYC_OPTIONS.find((type) => type.value === field.value)
                        ?.name || ""
                    }
                    getOptionLabel={(option) => option.name || option}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>
          </Show.When>

          <Grid2 size={12}>
            <Controller
              name="customizeSetting.riskprofiling"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <GKCheckbox
                      disableLeftPadding
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={"Include risk profiling in onboarding flow"}
                  labelPlacement="end"
                />
              )}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              name="customizeSetting.agreement"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <GKCheckbox
                      disableLeftPadding
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={"Include agreement in onboarding flow"}
                  labelPlacement="end"
                />
              )}
            />
          </Grid2>

          <Show.When isTrue={form.watch("customizeSetting.agreement")}>
            <Grid2 size={6}>
              <Controller
                name="agreementTemplate"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <GKSearchSelect
                    {...field}
                    loading={customAgreementLoading}
                    inputLabel="Agreement Template"
                    disableClearable={false}
                    options={customAgreementTemplate}
                    value={
                      customAgreementTemplate?.find(
                        (dataItem) => dataItem.value === field.value,
                      )?.name || ""
                    }
                    onChange={(event, val) =>
                      field.onChange(val ? val?.value : "")
                    }
                    getOptionLabel={(option) => option.name || option}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="agreement"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please select agreement",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <GKSearchSelect
                    {...field}
                    disableClearable
                    requiredField
                    inputLabel="Agreement Sign Type"
                    options={AGREEMENT_SIGNATURE_SOURCE}
                    onChange={(event, val) =>
                      field.onChange(val?.value || null)
                    }
                    value={
                      AGREEMENT_SIGNATURE_SOURCE.find(
                        (type) => type.value === field.value,
                      )?.name || []
                    }
                    getOptionLabel={(option) => option.name || option}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>
          </Show.When>
        </Grid2>

        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Default Portfolio code
          </Typography>
        </Grid2>

        <Grid2
          container
          size={9}
          spacing={2}
          display={"flex"}
          alignItems={"center"}
        >
          <Grid2 size={4}>
            <Controller
              name="codePrefix"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter prefix",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  requiredField
                  inputLabel="Prefix"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={8} display={"flex"} alignItems={"center"} gap={2}>
            <Typography>Code Length</Typography>
            <Controller
              name={"codeLength"}
              control={form.control}
              render={({ field }) => (
                <FormControl component="fieldset">
                  <RadioGroup
                    {...field}
                    onChange={(e) => field.onChange(+e.target.value)}
                    value={+field.value}
                  >
                    <Grid2
                      container
                      size={12}
                      display={"flex"}
                      alignItems={"center"}
                    >
                      <Grid2>
                        <FormControlLabel
                          value={3}
                          control={<GKRadio />}
                          label={<Typography>3-digits</Typography>}
                          sx={{ mr: "8px" }}
                        />
                      </Grid2>
                      <Grid2>
                        <FormControlLabel
                          value={4}
                          control={<GKRadio />}
                          label={<Typography>4-digits</Typography>}
                          sx={{ mr: "8px" }}
                        />
                      </Grid2>
                      <Grid2>
                        <FormControlLabel
                          value={5}
                          control={<GKRadio />}
                          label={<Typography>5-digits</Typography>}
                        />
                      </Grid2>
                    </Grid2>
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Grid2>

          {prefixWatch && (
            <Grid2
              size={12}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-start"}
              gap={1}
            >
              <Typography
                fontSize={12}
                fontWeight={400}
                color={"var(--neutral-500)"}
              >
                Portfolio code will be
              </Typography>
              <Typography fontWeight={500}>{`${form.getValues(
                "codePrefix",
              )}${"0".repeat(form.getValues("codeLength"))}`}</Typography>
            </Grid2>
          )}
          <Grid2 size={4}>
            <Controller
              name="invoiceSeries"
              control={form.control}
              render={({ field }) => {
                return <GKTextField {...field} inputLabel={"Invoice Series"} />;
              }}
            />{" "}
          </Grid2>
          <Grid2 size={8}>
            <Controller
              name="revenueWithGst"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormControl>
                    <FormLabel>Revenue with GST</FormLabel>
                    <RadioGroup
                      {...field}
                      value={String(field.value)}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      <FormControlLabel
                        value="true"
                        control={<GKRadio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="false"
                        control={<GKRadio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                );
              }}
            />{" "}
          </Grid2>
        </Grid2>
        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>
        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Payment Method
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={6}>
            <Controller
              name="paymentMethods"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please select payment collection method",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  inputLabel="Payment Collection Method"
                  disableClearable={false}
                  options={PAYMENT_METHOD}
                  value={
                    PAYMENT_METHOD?.find(
                      (dataItem) => dataItem.value === field.value,
                    )?.name || ""
                  }
                  onChange={(event, val) => {
                    field.onChange(val?.value);
                  }}
                  getOptionLabel={(option) => option.name || option}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12} display={"flex"} justifyContent={"flex-end"}>
          <GKButton
            type="submit"
            variant="contained"
            loading={updateInvestorLoading}
          >
            Save
          </GKButton>
        </Grid2>
      </Grid2>
    </form>
  );
};

export default WebsiteConfigurations;
